import React, { useCallback } from 'react';
import { omit } from 'underscore';

import VideoTemplateEditor, {
  ActionType,
  OnIsAutoFrameChange,
  TemplateAction,
  UseVideoTemplateEditorConfig,
  useVideoTemplateEditorState,
  VideoTemplateState,
} from 'components/VideoTemplateEditor';
import {
  useIntroOutroUploadIntegration,
  useMediaUploadIntegration,
  useStaticTextIntegration,
} from 'components/VideoTemplateEditor/integrations';
import useDynamicVideoIntegration from 'components/VideoTemplateEditor/integrations/useDynamicVideoIntegration';
import { PlaceholderVideo } from 'components/VideoTemplateEditor/types/integrations';
import { Style } from 'components/VideoTemplateEditor/useOnStyleChange';
import { MediaImportedArgs } from 'containers/MediaUploadModal/types';
import TemplateStep from 'containers/ProjectWizards/TemplateStep';
import { TranscriptionFormValue } from 'containers/TranscriptionForm';
import useCustomFontsLoader from 'hooks/useCustomFontsLoader';
import useLastUsedStyleDispatch from 'hooks/useLastUsedStyleDispatch';
import useLastUsedStyleSelector from 'hooks/useLastUsedStyleSelector';
import { Source } from 'redux/middleware/api/media-upload-service';
import { AspectRatioName, Omit, TemplateType, VideoTypes } from 'types';

import { block } from './utils';

export interface VideoWizardCustomizeStepProps
  extends Omit<
    UseVideoTemplateEditorConfig,
    'templateId' | 'lastUsedStyle' | 'onStyleChange'
  > {
  aspectRatioName: AspectRatioName;
  view: 'template' | 'customize';
  templateId?: string;
  transcription?: TranscriptionFormValue;
  audioClipDurationSec?: number;
  ctaLabel?: string;
  mainVideoSrc?: Source;
  mediaImportedArgs?: MediaImportedArgs;
  placeholderVideo?: PlaceholderVideo;
  onIsAutoFrameChange?: OnIsAutoFrameChange;
  onTranscriptionChange: (transcription: TranscriptionFormValue) => void;
  onTemplateSelect: (templateId?: string) => void;
  onChangeAspectRatio: (aspectRatio: AspectRatioName) => void;
}

const VideoWizardCustomizeStep: React.FC<VideoWizardCustomizeStepProps> = ({
  defaults,
  aspectRatioName,
  view,
  templateId,
  isFullEpisode,
  transcription,
  audioClipDurationSec,
  ctaLabel,
  mainVideoSrc,
  mediaImportedArgs,
  placeholderVideo,
  features: baseFeatures,
  onError,
  onIsAutoFrameChange,
  onTranscriptionChange,
  onTemplateSelect,
  onChangeAspectRatio,
  onSubmit: onSubmitProp,
}) => {
  const { setLastUsedStyle } = useLastUsedStyleDispatch('videoTranscription');
  const { lastUsedStyle } = useLastUsedStyleSelector('videoTranscription');

  const handleStyleChange = useCallback(
    (style: Style, actionType: ActionType): void => {
      if (actionType === 'TRANSCRIPTION_CHANGE') {
        onTranscriptionChange(style.transcription);
      }

      setLastUsedStyle(omit(style, 'image', 'video'));
    },
    [onTranscriptionChange, setLastUsedStyle],
  );

  const {
    state,
    template,
    features,
    imageProcessorStatus,
    onChange,
    onSubmit,
  } = useVideoTemplateEditorState({
    defaults,
    templateId,
    isFullEpisode,
    transcription,
    lastUsedStyle,
    features: baseFeatures,
    initializeSoundwave: false,
    onError,
    onStyleChange: handleStyleChange,
    onSubmit: onSubmitProp,
    onTemplatesClick() {
      onTemplateSelect(undefined);
    },
  });

  // Preloads the fonts for getting the font styles when mounting the customize step.
  useCustomFontsLoader();

  const handleChange = useCallback(
    (
      stateUpdater: (prevState: VideoTemplateState) => VideoTemplateState,
      action: TemplateAction,
    ) => {
      onChange(stateUpdater, action);
    },
    [onChange],
  );

  const dynamicVideoIntegration = useDynamicVideoIntegration({
    isActive: view === 'customize',
    placeholderVideo,
    state,
    template,
    priority: 0,
    onIsAutoFrameChange,
    onChange: handleChange,
  });

  const integrations = [
    dynamicVideoIntegration,
    useMediaUploadIntegration({ priority: 1 }),
    useStaticTextIntegration({
      priority: 2,
    }),
    useIntroOutroUploadIntegration({ priority: 3 }),
  ];

  return view === 'template' ? (
    <TemplateStep
      {...{ aspectRatioName, onChangeAspectRatio }}
      alwaysShowTemplatesWithCaptions
      initialSubStep="template-selection"
      sourceMediaType="video"
      onSelect={onTemplateSelect}
      templateTypes={[
        'blank',
        TemplateType.HEADLINER_DEFAULT,
        TemplateType.USER_GENERATED,
      ]}
      compatibilityTypes={
        isFullEpisode ? [VideoTypes.FULL_EPISODE] : [VideoTypes.SHORT_CLIP]
      }
    />
  ) : (
    <div className={block()}>
      <VideoTemplateEditor
        {...{
          state,
          integrations,
          features,
          transcription,
          audioClipDurationSec,
          ctaLabel,
        }}
        media={{
          source: mainVideoSrc,
          type: 'video',
          mediaImportedArgs,
        }}
        eddyProjectVisibility={isFullEpisode ? 'hidden' : 'visible'}
        projectName={
          typeof mainVideoSrc !== 'string' ? mainVideoSrc?.name : undefined
        }
        allowCaptionsManagement={isFullEpisode}
        isFullEpisode={isFullEpisode}
        loading={imageProcessorStatus === 'processing'}
        className={block('editor')}
        showControls
        onChange={handleChange}
        onCtaClick={onSubmit}
      />
    </div>
  );
};

export default VideoWizardCustomizeStep;
